// @flow

import UserContext from "../utils/UserContext";
import { API } from "aws-amplify";

import React from "react";
import { useContext, useEffect, useState } from "react";
import { Container, Row, Col } from "react-bootstrap";
import Modal from "react-bootstrap/Modal";
import Form from "react-bootstrap/Form";
import Button from "react-bootstrap/Button";
import Spinner from "react-bootstrap/Spinner";
import Dropdown from "react-bootstrap/Dropdown";
import DropdownButton from "react-bootstrap/DropdownButton";

import {
  queryScriptSheetsBySessionSheetsIdIndex,
  queryBookmarksBySessionIdRowIndex,
  getSession,
} from "./../graphql/queries";

import {
  createScriptSheets as createScriptSheetsMutation,
  createStoredSheetsData as createStoredSheetsDataMutation,
  updateSession,
  deleteScriptSheets as deleteScriptSheetsMutation,
  updateSession as updateSessionMutation,
  deleteBookmarks as deleteBookmarksMutation,
} from "./../graphql/mutations";

import { onUpdateSession } from "../graphql/subscriptions";

import "./../index.css";

type Props = $ReadOnly<{
  sessionId: string,
  sessionName: string,
  show: boolean,
  onHide: (boolean) => void,
  setSheets: React.Dispatch<React.SetStateAction<any>>,
  sheets: array,
}>;

async function fetchScriptSheets(sessionId) {
  try {
    const apiData = await API.graphql({
      query: queryScriptSheetsBySessionSheetsIdIndex,
      variables: {
        sessionSheetsId: sessionId,
      },
    });
    return apiData.data.queryScriptSheetsBySessionSheetsIdIndex.items;
  } catch (e) {
    console.log(e);
  }
}

async function deleteScriptSheets(id: string) {
  const ret = await API.graphql({
    query: deleteScriptSheetsMutation,
    variables: {
      input: {
        id: id,
      },
    },
  });
}

async function deleteBookmark(id: string, row: number) {
  const ret = await API.graphql({
    query: deleteBookmarksMutation,
    variables: {
      input: {
        id: id,
        row: row,
      },
    },
  });
}

function colorCodeToRGB(colorCode) {
  if (colorCode) {
    if (!colorCode.startsWith("#")) {
      throw new Error("Invalid color code format");
    }

    const hex = colorCode.slice(1); // Remove the '#' symbol

    if (hex.length !== 6) {
      throw new Error("Invalid color code length");
    }

    const red = (parseInt(hex.slice(0, 2), 16) / 255).toFixed(3);
    const green = (parseInt(hex.slice(2, 4), 16) / 255).toFixed(3);
    const blue = (parseInt(hex.slice(4, 6), 16) / 255).toFixed(3);

    return { red: red, green: green, blue: blue };
  }
}

export default function SheetsLoadView(props: Props): React$Element<any> {
  const [isButtonDisabled, setIsButtonDisabled] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [sheetsUrl, setSheetsUrl] = useState("");
  const [sheetName, setSheetName] = useState("");
  const [sheetNameArray, setSheetNameArray] = useState([]);
  const [spreadsheetsName, setSpreadsheetsName] = useState(
    "Select a spreadsheet",
  );
  const [spreadsheetsId, setSpreadsheetsId] = useState("");
  const [spreadsheetsArray, setSpreadsheetsArray] = useState([]);
  const [updateCount, setUpdateCount] = useState(0);
  const [hasOauthInfo, setHasOauthInfo] = useState(false);
  const [oauthSheetNameArray, setOauthSheetNameArray] = useState([]);
  const [oauthSheetName, setOauthSheetName] = useState("");
  const [deleteBookmarksChecked, setDeleteBookmarksChecked] = useState(true);

  const userContext = useContext(UserContext);
  const accessToken = localStorage.getItem("accessToken");

  // SheetsUrl, updateCount initial fetch
  useEffect(() => {
    async function initialFetch() {
      const fetchedSession = await fetchSheetsSession(props.sessionId);
      setSheetsUrl(fetchedSession.sheetsUrl);
      setUpdateCount(fetchedSession.sheetsUpdateCount);
    }
    initialFetch();
  }, [props.sessionId]);

  // Spreadsheets list initial fetch for OAuth users
  useEffect(() => {
    if (accessToken) {
      handleGetList();
    }
  }, []);

  // Sheet name auto fetch for Url
  useEffect(() => {
    if (sheetsUrl && sheetsUrl !== "") {
      fetchSheetNames(sheetsUrl.split("/")[5]);
    }
  }, [sheetsUrl]);

  // Spreadsheets names auto fetch
  useEffect(() => {
    if (spreadsheetsId && spreadsheetsId !== "") {
      oauthFetchSheetNames(spreadsheetsId);
    }
  }, [spreadsheetsId]);

  // Subscribe to sheets url / updateCount update
  useEffect(() => {
    const subscription = API.graphql({
      query: onUpdateSession,
      variables: {
        id: props.sessionId,
      },
    }).subscribe({
      next: () => {
        async function updateFetch() {
          const fetchedSession = await fetchSheetsSession();
          setSheetsUrl(fetchedSession.sheetsUrl);
          setUpdateCount(fetchedSession.sheetsUpdateCount);
        }
        updateFetch();
      },
      error: (error) => console.warn(error),
    });
    return () => {
      subscription.unsubscribe();
    };
  }, []);

  // Subscribe to session sheets update count
  useEffect(() => {
    const subscription = API.graphql({
      query: onUpdateSession,
      variables: {
        id: props.sessionId,
      },
    }).subscribe({
      next: () => {
        async function updateFetch() {
          const fetchedSheets = await fetchScriptSheets(props.sessionId);
          props.setSheets(fetchedSheets);
        }
        updateFetch();
      },
      error: (error) => console.warn(error),
    });
    return () => {
      subscription.unsubscribe();
    };
  }, []);

  // Check if the user has OAuth info
  useEffect(() => {
    checkOauthInfo();
  }, []);

  async function fetchSheetsSession() {
    try {
      const apiData = await API.graphql({
        query: getSession,
        variables: {
          id: props.sessionId,
        },
      });
      return apiData.data.getSession;
    } catch (e) {
      console.log(e);
    }
  }

  async function updateSessionSheetsUrl(url: string) {
    if (props.sessionId === "" || userContext.userId === "") {
      return;
    }
    const ret = await API.graphql({
      query: updateSessionMutation,
      variables: {
        input: {
          id: props.sessionId,
          sheetsUrl: url,
        },
      },
    });
  }

  async function updateSessionSheetsUpdateCount() {
    if (props.sessionId === "" || userContext.userId === "") {
      return;
    }
    const ret = await API.graphql({
      query: updateSessionMutation,
      variables: {
        input: {
          id: props.sessionId,
          sheetsUpdateCount: 0,
        },
      },
    });
  }

  async function createScriptSheets(
    sessionId: string,
    name: string,
    script: string,
    translation: string,
    index: number,
    backgroundColor: string,
    textColor: string,
  ) {
    const ret = await API.graphql({
      query: createScriptSheetsMutation,
      variables: {
        input: {
          sessionSheetsId: sessionId,
          name: name,
          script: script,
          translation: translation,
          isSelected: false,
          isEditable: false,
          index: index,
          backgroundColor: backgroundColor,
          textColor: textColor,
        },
      },
    });
  }

  async function deleteAllScriptSheets(id: string) {
    const BATCH_SIZE = 25;
    let after = undefined;
    do {
      const apiData = await API.graphql({
        query: queryScriptSheetsBySessionSheetsIdIndex,
        variables: { sessionSheetsId: id, after },
      });
      const result = apiData.data.queryScriptSheetsBySessionSheetsIdIndex;
      const items = result.items;
      for (let i = 0; i < items.length; i += BATCH_SIZE) {
        await Promise.all(
          items
            .slice(i, i + BATCH_SIZE)
            .map((line) => deleteScriptSheets(line.id)),
        );
      }
      after = result.nextToken;
    } while (after);
  }

  async function deleteAllBookmarks() {
    try {
      const apiData = await API.graphql({
        query: queryBookmarksBySessionIdRowIndex,
        variables: {
          sessionId: props.sessionId,
        },
      });
      const bookmarks = apiData.data.queryBookmarksBySessionIdRowIndex.items;
      bookmarks.forEach((bookmark) => {
        deleteBookmark(bookmark.id, bookmark.row);
      });
    } catch (e) {
      console.log(e);
    }
  }

  async function createStoredSheetsData(
    sessionId: string,
    sessionName: string,
    sheetName: string,
    sheetsData: Record<string, any>,
  ) {
    try {
      const ret = await API.graphql({
        query: createStoredSheetsDataMutation,
        variables: {
          input: {
            sessionId: sessionId,
            sessionName: sessionName,
            sheetName: sheetName,
            sheetsData: JSON.stringify(sheetsData),
          },
        },
      });
      return ret.data?.createStoredSheetsData;
    } catch (e) {
      console.log(e);
    }
  }

  const componentToHex = (c) => {
    const int = Math.round(c * 255);
    const hex = int.toString(16);
    return hex.length == 1 ? "0" + hex : hex;
  };

  const rgbToHex = (r, g, b) => {
    return "#" + componentToHex(r) + componentToHex(g) + componentToHex(b);
  };

  const checkOauthInfo = () => {
    const info = JSON.parse(localStorage.getItem("authInfo"));
    if (info && info.refresh_token) {
      setHasOauthInfo(true);
    } else {
      setHasOauthInfo(false);
    }
  };

  const makeHeaders = () => {
    const headers = { Accept: "application/json" };
    if (accessToken) {
      headers.Authorization = `Bearer ${accessToken}`;
    }
    return headers;
  };

  async function getDriveFiles() {
    const apiKey = process.env.REACT_APP_GOOGLE_API_KEY;
    const itemsCount = 20;
    const apiUrl = `https://www.googleapis.com/drive/v3/files?key=${apiKey}&q=mimeType%3D'application%2Fvnd.google-apps.spreadsheet'&pageSize=${itemsCount}&trashed=false&orderBy=viewedByMeTime desc`;

    try {
      const response = await fetch(apiUrl, {
        method: "GET",
        headers: makeHeaders(),
      });
      if (!response.ok) {
        throw new Error(`Request failed with status: ${response.status}`);
      }
      return await response.json();
    } catch (error) {
      throw error;
    }
  }

  const handleGetList = () => {
    getDriveFiles()
      .then((res) => {
        const list = res.files;
        // console.log("List of files:", list);
        setSpreadsheetsArray(list);
      })
      .catch((error) => {
        console.error("Error:", error);
      });
  };

  // Fetch every sheet's name
  const fetchSheetNames = (sheetsId) => {
    const apiKey = process.env.REACT_APP_GOOGLE_API_KEY;
    const url = `https://sheets.googleapis.com/v4/spreadsheets/${sheetsId}?includeGridData=false&key=${apiKey}`;

    fetch(url)
      .then((res) => res.text())
      .then((rep) => {
        const jsonData = JSON.parse(rep).sheets;
        // const jsonData = JSON.parse(rep);

        const newArray = [];

        for (let idx = 0; idx < jsonData.length; idx++) {
          const title = jsonData[idx].properties.title;
          newArray.push(title);
          if (idx === 0) {
            setSheetName(title);
          }
        }
        setSheetNameArray(newArray);
      });
  };

  // Fetch every sheet's name
  const oauthFetchSheetNames = (sheetsId) => {
    const apiKey = process.env.REACT_APP_GOOGLE_API_KEY;
    const url = `https://sheets.googleapis.com/v4/spreadsheets/${sheetsId}?includeGridData=false&key=${apiKey}`;

    fetch(url, {
      method: "GET",
      headers: makeHeaders(),
    })
      .then((res) => res.text())
      .then((rep) => {
        const jsonData = JSON.parse(rep).sheets;
        // const jsonData = JSON.parse(rep);

        const newArray = [];

        for (let idx = 0; idx < jsonData.length; idx++) {
          const title = jsonData[idx].properties.title;
          newArray.push(title);
          if (idx === 0) {
            setOauthSheetName(title);
          }
        }
        setOauthSheetNameArray(newArray);
      });
  };

  // Import google sheets function
  const fetchGoogleSheets = async (sheetsId, sheetName) => {
    // const sheetsId = sheetsUrl.split("/")[5];
    const apiKey = process.env.REACT_APP_GOOGLE_API_KEY;
    const range = `'${sheetName}'!A:C`;
    const url = `https://sheets.googleapis.com/v4/spreadsheets/${sheetsId}?includeGridData=true&ranges=${range}&key=${apiKey}`;

    await deleteAllScriptSheets(props.sessionId);
    deleteBookmarksChecked && deleteAllBookmarks();

    const res = await fetch(url, { method: "GET", headers: makeHeaders() });
    const rep = await res.text();

    // Remove additional text and extract only JSON:
    const newArray = [];
    const forStoreData = [];
    const jsonData = JSON.parse(rep).sheets[0].data[0].rowData;
    const hiddenData = JSON.parse(rep).sheets[0].data[0].rowMetadata;

    // Iterate through fetched rows and push them into newArray
    for (let idx = 0; idx < jsonData.length; idx++) {
      const row = jsonData[idx];
      if (
        !hiddenData[idx].hasOwnProperty("hiddenByUser") // Condition to filter hidden rows
        // Conditions to filter out empty rows
        // &&
        // (row.values[0].hasOwnProperty("formattedValue") ||
        //   row.values[1].hasOwnProperty("formattedValue"))
      ) {
        newArray.push(row);
      }
      // console.log(row);
    }
    // console.log(newArray);

    // 본쇼 대본 아래에 빈 행이 1000개 이상 붙어있는 경우 좌측 시트 렌더가 무거워지므로 trailing 빈 행을 잘라낸다
    const hasContent = (rowEntry) => {
      if (!rowEntry.hasOwnProperty("values")) return false;
      const rowData = rowEntry.values;
      const cellHasValue = (cell) =>
        cell &&
        cell.hasOwnProperty("formattedValue") &&
        cell.formattedValue !== "";
      return cellHasValue(rowData[0]) || cellHasValue(rowData[1]) || cellHasValue(rowData[2]);
    };
    let lastContentIdx = -1;
    for (let i = newArray.length - 1; i >= 0; i--) {
      if (hasContent(newArray[i])) {
        lastContentIdx = i;
        break;
      }
    }
    newArray.length = lastContentIdx + 1;

    // Build thunks for each row — executed in batches below
    const createQueue = newArray.map((rowEntry, idx) => {
      // (25/11/24) Sparse indexing w/ 1,000 spacing between rows
      const indexNum = (idx + 1) * 1000;
      const denseRow = idx + 1;

      if (rowEntry.hasOwnProperty("values")) {
        const rowData = rowEntry.values;
        // console.log(rowData);

        let nameData;
        if (
          rowData.length === 0 ||
          !rowData[0].hasOwnProperty("formattedValue")
        ) {
          nameData = "";
        } else {
          nameData = rowData[0].formattedValue;
        }

        let scriptData;
        if (
          rowData.length === 0 ||
          rowData.length === 1 ||
          !rowData[1].hasOwnProperty("formattedValue")
        ) {
          scriptData = "";
        } else {
          scriptData = rowData[1].formattedValue;
        }

        let translationData;
        if (
          rowData.length === 0 ||
          rowData.length === 1 ||
          rowData.length === 2 ||
          !rowData[2].hasOwnProperty("formattedValue")
        ) {
          translationData = "";
        } else {
          translationData = rowData[2].formattedValue;
        }

        let backgroundColorObj;
        if (
          rowData.length === 0 ||
          rowData.length === 1 ||
          !rowData[1].hasOwnProperty("effectiveFormat")
        ) {
          backgroundColorObj = { red: 1, green: 1, blue: 1 };
        } else {
          backgroundColorObj = rowData[1].effectiveFormat.backgroundColor;
        }

        let textColorObj;
        if (
          rowData.length === 0 ||
          rowData.length === 1 ||
          !rowData[1].hasOwnProperty("effectiveFormat")
        ) {
          textColorObj = {};
        } else {
          textColorObj = rowData[1].effectiveFormat.textFormat.foregroundColor;
        }

        const backgroundColorHex = rgbToHex(
          backgroundColorObj.red ? backgroundColorObj.red : 0,
          backgroundColorObj.green ? backgroundColorObj.green : 0,
          backgroundColorObj.blue ? backgroundColorObj.blue : 0,
        );

        const textColorHex = rgbToHex(
          textColorObj.red ? textColorObj.red : 0,
          textColorObj.green ? textColorObj.green : 0,
          textColorObj.blue ? textColorObj.blue : 0,
        );

        forStoreData.push({
          row: denseRow,
          name: nameData,
          script: scriptData,
          translation: translationData,
        });

        return () =>
          createScriptSheets(
            props.sessionId,
            nameData,
            scriptData,
            translationData,
            indexNum,
            backgroundColorHex,
            textColorHex,
          );
      } else {
        return () =>
          createScriptSheets(
            props.sessionId,
            "",
            "",
            "",
            indexNum,
            "#FFFFFF",
            "#000000",
          );
      }
    });

    // Execute creates in batches of 25 to avoid ERR_INSUFFICIENT_RESOURCES
    const BATCH_SIZE = 25;
    for (let i = 0; i < createQueue.length; i += BATCH_SIZE) {
      await Promise.all(createQueue.slice(i, i + BATCH_SIZE).map((fn) => fn()));
    }

    // TODO : Create a function to save the whole array into one item in translations pair store database
    // ✅ 저장/로그는 forStoreData 기준 (AWSJSON은 createStoredSheetsData에서 stringify함)
    // const s = JSON.stringify(forStoreData);
    // console.log(s);
    // console.log("json bytes:", new Blob([s]).size);
    // console.log("session name:", props.sessionName);
    // console.log("sheet name:", sheetName);

    // AWSJSON: createStoredSheetsData 내부에서 JSON.stringify 하니까 배열 그대로 넘기면 됨
    const savedData = createStoredSheetsData(
      props.sessionId,
      props.sessionName,
      sheetName,
      forStoreData,
    );
    console.log(savedData);

    await updateSessionSheetsUpdateCount();
  };

  async function createNewSheet() {
    const url = `https://sheets.googleapis.com/v4/spreadsheets/${spreadsheetsId}:batchUpdate`;

    const requestData = {
      requests: [
        {
          addSheet: {
            properties: {
              title: `Comet_${oauthSheetName}`,
            },
          },
        },
      ],
    };

    try {
      const response = await fetch(url, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(requestData),
      });

      const data = await response.json();
      return data.replies[0].addSheet.properties.sheetId;
    } catch (error) {
      console.error("Error creating sheet:", error);
      window.alert(
        "An error occurred. Please check if the sheet name is already in use.",
      );
      throw error; // Rethrow the error to handle it outside this function if needed
    }
  }

  function batchUpdateValues(values) {
    const range = `Comet_${oauthSheetName}!A:C`;

    const data = [];
    data.push({
      range: range,
      majorDimension: "ROWS",
      values: values,
    });

    const valueInputOption = "RAW";

    const body = {
      data: data,
      valueInputOption: valueInputOption,
    };

    const url = `https://sheets.googleapis.com/v4/spreadsheets/${spreadsheetsId}/values:batchUpdate`;

    fetch(url, {
      method: "POST",
      headers: makeHeaders(),
      body: JSON.stringify(body),
    })
      .then((response) => response.json())
      .then((data) => {
        console.log(data);
      })
      .catch((error) => {
        console.error(error);
      });
  }

  const batchUpdateStyle = (sheetId, rowCount, styles) => {
    const range = {
      sheetId: sheetId,
      startRowIndex: 0,
      endRowIndex: rowCount,
      startColumnIndex: 0,
      endColumnIndex: 3,
    };

    const url = `https://sheets.googleapis.com/v4/spreadsheets/${spreadsheetsId}:batchUpdate`;

    const requestData = {
      requests: [
        {
          updateCells: {
            range: range,
            fields:
              "userEnteredFormat.textFormat,userEnteredFormat.backgroundColor",
            rows: styles,
          },
        },
      ],
    };

    fetch(url, {
      method: "POST",
      headers: makeHeaders(),
      body: JSON.stringify(requestData),
    })
      .then((response) => response.json())
      .then((data) => {
        console.log("Batch update successful:", data);
      })
      .catch((error) => {
        console.error("Error in batch update:", error);
      });
  };

  const overwriteGoogleSheets = () => {
    async function updateFetch() {
      const newSheetId = await createNewSheet();
      const fetchedSheets = await fetchScriptSheets(props.sessionId);
      // console.log(fetchedSheets.length);

      let values = [];
      let styles = [];
      let rowCount = fetchedSheets.length;

      fetchedSheets.forEach((row) =>
        values.push([row.name, row.script, row.translation]),
      );

      fetchedSheets.forEach((row) =>
        styles.push({
          values: [
            {
              userEnteredFormat: {
                textFormat: {
                  foregroundColor: colorCodeToRGB(row.textColor),
                },
                backgroundColor: colorCodeToRGB(row.backgroundColor),
              },
            },
            {
              userEnteredFormat: {
                textFormat: {
                  foregroundColor: colorCodeToRGB(row.textColor),
                },
                backgroundColor: colorCodeToRGB(row.backgroundColor),
              },
            },
            {
              userEnteredFormat: {
                textFormat: {
                  foregroundColor: colorCodeToRGB(row.textColor),
                },
                backgroundColor: colorCodeToRGB(row.backgroundColor),
              },
            },
          ],
        }),
      );

      batchUpdateValues(values);
      batchUpdateStyle(newSheetId, rowCount, styles);
    }
    updateFetch();
  };

  const handleFetchClick = async () => {
    setIsLoading(true);
    setIsButtonDisabled(true);
    try {
      if (hasOauthInfo) {
        await fetchGoogleSheets(spreadsheetsId, oauthSheetName);
      } else {
        await fetchGoogleSheets(sheetsUrl.split("/")[5], sheetName);
      }
      props.onHide(false);
    } finally {
      setIsLoading(false);
      setIsButtonDisabled(false);
    }
  };

  const handleOverwriteClick = () => {
    overwriteGoogleSheets();
    setIsButtonDisabled(true);
    setTimeout(() => {
      setIsButtonDisabled(false);
    }, 3000);
    setTimeout(updateSessionSheetsUpdateCount, 3000);
  };

  const confirmModifiedFetch = () => {
    if (updateCount === 1) {
      const confirmFetch = () => {
        if (
          window.confirm(
            `Wait! There was a change in the sheet after the previous load.` +
              `\n` +
              `Are you sure to load Google Sheets?`,
          )
        ) {
          handleFetchClick();
        } else {
          return;
        }
      };
      confirmFetch();
    } else if (updateCount === 0) {
      handleFetchClick();
    }
  };

  const confirmOverwrite = () => {
    if (window.confirm(`Copy session sheets to Google Sheets?`)) {
      handleOverwriteClick();
    } else {
      return;
    }
  };

  return (
    <Modal
      show={props.show}
      onHide={() => props.onHide(false)}
      dialogClassName="modal-60w"
    >
      <Modal.Header closeButton>
        <Modal.Title>Load Google Sheets</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Container className="p-0 m-0">
          {hasOauthInfo ? (
            <Row className="mt-0">
              <Row className="mb-3">
                <Col xs={8}>
                  <h5>Choose From Your Spreadsheets :</h5>
                </Col>
                <DropdownButton
                  id="dropdown-item-button"
                  title={<span>{spreadsheetsName}</span>}
                  variant="light"
                >
                  {spreadsheetsArray.map((value, index) => (
                    <Dropdown.Item
                      as="button"
                      // style={{ overflow: "scroll" }}
                      key={index}
                      onClick={() => {
                        setSpreadsheetsId(value.id);
                        setSpreadsheetsName(value.name);
                        oauthFetchSheetNames(value.id);
                      }}
                    >
                      <span>{value.name}</span>
                    </Dropdown.Item>
                  ))}
                </DropdownButton>
              </Row>
              <Row>
                <Col xs={8}>
                  <h6>Sheet Name :</h6>
                </Col>
                <DropdownButton
                  id="dropdown-item-button"
                  title={<span>{oauthSheetName}</span>}
                  variant="light"
                >
                  {oauthSheetNameArray.map((value, index) => (
                    <Dropdown.Item
                      as="button"
                      key={index}
                      onClick={() => {
                        setOauthSheetName(value);
                      }}
                    >
                      <span>{value}</span>
                    </Dropdown.Item>
                  ))}
                </DropdownButton>
              </Row>
            </Row>
          ) : (
            <Container>
              <Row>
                <Col style={{}}>
                  <h5>Paste Google Sheets URL Here :</h5>
                </Col>
              </Row>
              <Row>
                <Col>
                  <Form.Control
                    type="text"
                    placeholder="Google Sheets URL"
                    onChange={(event) => {
                      updateSessionSheetsUrl(event.target.value);
                    }}
                    defaultValue={sheetsUrl}
                  />
                </Col>
              </Row>
              <br />
              <Row>
                <Col xs={8}>
                  <h6>Sheet Name :</h6>
                </Col>
                <DropdownButton
                  id="dropdown-item-button"
                  title={<span>{sheetName}</span>}
                  variant="light"
                >
                  {sheetNameArray.map((value, index) => (
                    <Dropdown.Item
                      as="button"
                      key={index}
                      onClick={() => {
                        setSheetName(value);
                      }}
                    >
                      <span>{value}</span>
                    </Dropdown.Item>
                  ))}
                </DropdownButton>
              </Row>
              <br />
              <Row>
                <span style={{ color: "#5e75ff", fontStyle: "italic" }}>
                  * Set Google Sheet's share option to{" "}
                  <b> 'Anyone with the link'</b>
                </span>
              </Row>
            </Container>
          )}

          <Row
            style={{
              color: "#5e75ff",
              fontStyle: "italic",
            }}
            className="mt-4 ms-0"
          >
            <Col className="mb-2" style={{ fontWeight: "bold" }}>
              * Please Keep The Format !!
            </Col>
            <Row>
              <Col>- Column A : Name</Col>
            </Row>
            <Row>
              <Col>- Column B : Script</Col>
            </Row>
            <Row>
              <Col>- Column C : Translation</Col>
            </Row>
          </Row>
          <Row
            style={{
              color: "#5e75ff",
              fontStyle: "italic",
            }}
            className="mt-3 ms-0"
          >
            <Col className="mb-2">
              * If the sheets doesn't show after load, please refresh the page
            </Col>
          </Row>
          <Row>
            <Col>
              <Form>
                <Form.Check
                  label="Delete previous bookmarks"
                  checked={deleteBookmarksChecked}
                  onChange={() => {
                    setDeleteBookmarksChecked((prev) => !prev);
                  }}
                />
              </Form>
            </Col>
          </Row>
          <Row>
            <Col>
              {hasOauthInfo ? (
                <Button
                  className="float-end"
                  variant="danger"
                  size="sm"
                  onClick={confirmOverwrite}
                  disabled={
                    isButtonDisabled ||
                    spreadsheetsId === "" ||
                    oauthSheetName === ""
                  }
                >
                  Write
                </Button>
              ) : null}
              <Button
                className="float-end me-2"
                variant="primary"
                size="sm"
                onClick={confirmModifiedFetch}
                disabled={
                  hasOauthInfo
                    ? isLoading ||
                      isButtonDisabled ||
                      spreadsheetsId === "" ||
                      oauthSheetName === ""
                    : isLoading ||
                      isButtonDisabled ||
                      sheetsUrl === "" ||
                      sheetName === ""
                }
              >
                {isLoading ? (
                  <>
                    <Spinner
                      as="span"
                      animation="border"
                      size="sm"
                      role="status"
                      aria-hidden="true"
                      className="me-1"
                    />
                    Loading...
                  </>
                ) : (
                  "Load"
                )}
              </Button>
            </Col>
          </Row>
        </Container>
      </Modal.Body>
    </Modal>
  );
}
